import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from '../utils/axiosConfig';
import DateInput from '../components/DateInput';
import { formatCurrency } from '../utils/currency';
import { formatDate, todayIso } from '../utils/dates';
import { pickDefaultUom, effectivePurchasePrice, openPriceLayers } from '../utils/uom';
import KebabMenu, { MENU_ICONS, useKebabMenu } from '../components/KebabMenu';
import './Purchases.css';

const emptyItem = () => ({
  mode: 'existing', // existing | new
  product_id: '',
  description: '',
  quantity: 1,
  uom_id: '',
  unit_cost: 0,
  discount: 0,
  new_name: '',
  new_sku: '',
  new_price: 0,
  new_unit: 'pcs',
  sale_price: ''
});

function salePriceFor(product, uom) {
  const layers = openPriceLayers(product);
  const base = layers.length ? layers[layers.length - 1].sale_price : (parseFloat(product?.price) || 0);
  const factor = parseFloat(uom?.conversion_to_base) || 1;
  return Math.round(base * factor * 1000) / 1000;
}

function stockPriceHint(product, salePrice, uomId) {
  const layers = openPriceLayers(product);
  if (!layers.length) return '';
  const active = layers[0];
  const uom = (product.uoms || []).find((row) => row.id === uomId);
  const factor = parseFloat(uom?.conversion_to_base) || 1;
  const enteredBase = (parseFloat(salePrice) || 0) / (factor || 1);
  if (Math.abs(enteredBase - active.sale_price) < 0.009) return '';
  const qty = Math.round(active.qty_remaining * 1000) / 1000;
  const unit = product.unit || 'pcs';
  return `${qty} ${unit} still sell at ${formatCurrency(active.sale_price)}. After those, POS uses ${formatCurrency(enteredBase)} per ${unit}.`;
}

async function attachmentErrorMessage(error) {
  const data = error?.response?.data;
  if (data && typeof data === 'object' && typeof data.message === 'string') return data.message;
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    try {
      const text = await data.text();
      const parsed = JSON.parse(text);
      if (parsed && parsed.message) return parsed.message;
    } catch (parseError) {
      // Response was not JSON.
    }
  }
  return 'Failed to open attachment';
}

const emptyForm = () => ({
  supplier_id: '',
  purchase_date: todayIso(),
  discount: 0,
  notes: '',
  payment_status: 'pending',
  items: [emptyItem()]
});

const Purchases = () => {
  const [purchases, setPurchases] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingPurchase, setEditingPurchase] = useState(null);
  const [openActionMenu, setOpenActionMenu] = useKebabMenu();
  const [formData, setFormData] = useState(emptyForm());
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [pendingAttachment, setPendingAttachment] = useState(null);
  const [scanInfo, setScanInfo] = useState('');
  const [scannedSupplierName, setScannedSupplierName] = useState('');
  const [addingSupplier, setAddingSupplier] = useState(false);
  const [attachmentPreview, setAttachmentPreview] = useState(null);
  const fileInputRef = useRef(null);
  const previewUrlRef = useRef('');

  useEffect(() => {
    fetchAll();
  }, []);

  useEffect(() => () => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
  }, []);

  const fetchAll = async () => {
    try {
      const [purchasesRes, suppliersRes, productsRes] = await Promise.all([
        axios.get('/api/purchases'),
        axios.get('/api/suppliers'),
        axios.get('/api/products')
      ]);
      setPurchases(purchasesRes.data.purchases || []);
      setSuppliers(suppliersRes.data.suppliers || []);
      setProducts(productsRes.data.products || []);
    } catch (error) {
      console.error('Error loading purchases data:', error);
      alert('Failed to load purchases');
    } finally {
      setLoading(false);
    }
  };

  const lineTotal = (item) => {
    const qty = parseFloat(item.quantity) || 0;
    const cost = parseFloat(item.unit_cost) || 0;
    const disc = parseFloat(item.discount) || 0;
    return Math.max(0, qty * cost - disc);
  };

  const totals = useMemo(() => {
    const subtotal = (formData.items || []).reduce((sum, item) => sum + lineTotal(item), 0);
    const discount = parseFloat(formData.discount) || 0;
    return {
      subtotal,
      total: Math.max(0, subtotal - discount)
    };
  }, [formData.items, formData.discount]);

  const updateItem = (index, field, value) => {
    const items = [...formData.items];
    const previous = items[index];
    items[index] = { ...previous, [field]: value };

    if (field === 'product_id' && value) {
      const product = products.find((p) => p.id === value);
      if (product) {
        const uom = pickDefaultUom(product.uoms, 'purchase');
        items[index].description = product.name;
        items[index].uom_id = uom?.id || '';
        items[index].unit_cost = effectivePurchasePrice(uom, product.cost);
        items[index].sale_price = salePriceFor(product, uom);
      }
    }

    if (field === 'uom_id' && value) {
      const product = products.find((p) => p.id === items[index].product_id);
      const uom = (product?.uoms || []).find((u) => u.id === value);
      const prevUom = (product?.uoms || []).find((u) => u.id === previous.uom_id);
      if (uom) {
        items[index].unit_cost = effectivePurchasePrice(uom, product?.cost);
        const prevFactor = parseFloat(prevUom?.conversion_to_base) || 1;
        const nextFactor = parseFloat(uom.conversion_to_base) || 1;
        const current = parseFloat(previous.sale_price);
        items[index].sale_price = Number.isFinite(current)
          ? Math.round((current / prevFactor) * nextFactor * 1000) / 1000
          : salePriceFor(product, uom);
      }
    }

    if (field === 'mode') {
      items[index].product_id = '';
      items[index].description = '';
      items[index].new_name = '';
      items[index].new_sku = '';
      items[index].new_price = 0;
      items[index].new_unit = 'pcs';
      items[index].sale_price = '';
    }

    setFormData({ ...formData, items });
  };

  const addItem = () => {
    setFormData({ ...formData, items: [...formData.items, emptyItem()] });
  };

  const removeItem = (index) => {
    if (formData.items.length === 1) return;
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index)
    });
  };

  const clearAttachmentState = () => {
    setAttachmentFile(null);
    setPendingAttachment(null);
    setScanInfo('');
    setScannedSupplierName('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleAttachmentChange = (e) => {
    const file = e.target.files?.[0] || null;
    setAttachmentFile(file);
    setPendingAttachment(null);
    setScanInfo(file ? `Selected: ${file.name}` : '');
  };

  const handleScanBill = async () => {
    if (!attachmentFile) {
      alert('Choose a bill image or PDF first');
      return;
    }

    setScanning(true);
    setScanInfo('Scanning bill with Gemini…');
    try {
      const fd = new FormData();
      fd.append('attachment', attachmentFile);
      const res = await axios.post('/api/purchases/scan', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const scan = res.data.scan || {};
      const mappedItems = (scan.items || []).map((item) => {
        if (item.mode === 'existing') {
          const product = products.find((p) => p.id === item.product_id);
          const uom = pickDefaultUom(product?.uoms, 'purchase');
          return {
            mode: 'existing',
            product_id: item.product_id || '',
            description: item.description || '',
            quantity: item.quantity || 1,
            unit_cost: item.unit_cost || 0,
            discount: item.discount || 0,
            new_name: '',
            new_sku: '',
            new_price: 0,
            new_unit: 'pcs',
            uom_id: uom?.id || '',
            sale_price: product ? salePriceFor(product, uom) : ''
          };
        }
        return {
          mode: 'new',
          product_id: '',
          description: item.description || item.new_name || '',
          quantity: item.quantity || 1,
          unit_cost: item.unit_cost || 0,
          discount: item.discount || 0,
          new_name: item.new_name || item.description || '',
          new_sku: item.new_sku || '',
          new_price: item.new_price || 0,
          new_unit: item.new_unit || 'pcs'
        };
      });

      const existingCount = mappedItems.filter((i) => i.mode === 'existing').length;
      const newCount = mappedItems.filter((i) => i.mode === 'new').length;

      setPendingAttachment(res.data.pending_attachment || null);

      const hintName = String(scan.supplier_name || '').trim();
      if (scan.supplier_id) {
        setScannedSupplierName('');
      } else if (hintName) {
        setScannedSupplierName(hintName);
      } else {
        setScannedSupplierName('');
      }

      setFormData((prev) => ({
        ...prev,
        supplier_id: scan.supplier_id || prev.supplier_id,
        purchase_date: scan.purchase_date || prev.purchase_date,
        discount: scan.discount != null ? scan.discount : prev.discount,
        notes: scan.notes || prev.notes,
        items: mappedItems.length ? mappedItems : prev.items
      }));

      // Refresh products in case user wants to tweak matches
      const productsRes = await axios.get('/api/products');
      setProducts(productsRes.data.products || []);

      setScanInfo(
        `Scanned ${mappedItems.length} line(s): ${existingCount} existing, ${newCount} new` +
          (hintName
            ? (scan.supplier_id
              ? ` · Supplier matched: ${hintName}`
              : ` · New supplier on bill: ${hintName}`)
            : '')
      );
    } catch (error) {
      setScanInfo('');
      alert(error.response?.data?.message || 'Failed to scan bill');
    } finally {
      setScanning(false);
    }
  };

  const createSupplier = async (name) => {
    const supplierName = String(name || '').trim();
    if (!supplierName) {
      alert('Supplier name is required');
      return null;
    }
    setAddingSupplier(true);
    try {
      const res = await axios.post('/api/suppliers', {
        name: supplierName,
        status: 'active'
      });
      const created = res.data.supplier;
      const listRes = await axios.get('/api/suppliers');
      setSuppliers(listRes.data.suppliers || []);
      if (created?.id) {
        setFormData((prev) => ({ ...prev, supplier_id: created.id }));
        setScannedSupplierName('');
        setScanInfo((prev) => `${prev ? `${prev} · ` : ''}Supplier added: ${created.name}`);
      }
      return created;
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to add supplier');
      return null;
    } finally {
      setAddingSupplier(false);
    }
  };

  const handleAddScannedSupplier = async () => {
    await createSupplier(scannedSupplierName);
  };

  const handleAddSupplierManual = async () => {
    const name = window.prompt('New supplier name', scannedSupplierName || '');
    if (name == null) return;
    await createSupplier(name);
  };

  const buildItemsPayload = () =>
    formData.items.map((item) => {
      if (item.mode === 'new') {
        return {
          description: item.new_name || item.description,
          quantity: parseFloat(item.quantity) || 0,
          unit_cost: parseFloat(item.unit_cost) || 0,
          discount: parseFloat(item.discount) || 0,
          new_product: {
            name: item.new_name,
            sku: item.new_sku || undefined,
            price: parseFloat(item.new_price) || 0,
            cost: parseFloat(item.unit_cost) || 0,
            unit: item.new_unit || 'pcs'
          }
        };
      }
      return {
        product_id: item.product_id,
        description: item.description,
        quantity: parseFloat(item.quantity) || 0,
        uom_id: item.uom_id || undefined,
        unit_cost: parseFloat(item.unit_cost) || 0,
        discount: parseFloat(item.discount) || 0,
        sale_price: item.sale_price === '' || item.sale_price == null ? undefined : parseFloat(item.sale_price) || 0
      };
    });

  const handleSubmit = async (e) => {
    e.preventDefault();

    for (const item of formData.items) {
      if (item.mode === 'existing' && !item.product_id) {
        alert('Select a product for each existing-product line');
        return;
      }
      if (item.mode === 'new' && !String(item.new_name || '').trim()) {
        alert('Enter a name for each new product');
        return;
      }
      if (!(parseFloat(item.quantity) > 0)) {
        alert('Quantity must be greater than 0');
        return;
      }
    }

    setSaving(true);
    try {
      const items = buildItemsPayload();
      const useMultipart = Boolean(attachmentFile || pendingAttachment);

      if (useMultipart) {
        const fd = new FormData();
        if (formData.supplier_id) fd.append('supplier_id', formData.supplier_id);
        fd.append('purchase_date', formData.purchase_date);
        fd.append('discount', String(parseFloat(formData.discount) || 0));
        fd.append('notes', formData.notes || '');
        fd.append('payment_status', formData.payment_status || 'pending');
        fd.append('items', JSON.stringify(items));

        if (pendingAttachment?.filename) {
          fd.append('pending_attachment_filename', pendingAttachment.filename);
          fd.append('pending_attachment_original_name', pendingAttachment.original_name || '');
          fd.append('pending_attachment_mime', pendingAttachment.mime || '');
        } else if (attachmentFile) {
          fd.append('attachment', attachmentFile);
        }

        if (editingPurchase) {
          await axios.put(`/api/purchases/${editingPurchase.id}`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        } else {
          await axios.post('/api/purchases', fd, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        }
      } else {
        const payload = {
          supplier_id: formData.supplier_id || null,
          purchase_date: formData.purchase_date,
          discount: parseFloat(formData.discount) || 0,
          notes: formData.notes || '',
          payment_status: formData.payment_status || 'pending',
          items
        };
        if (editingPurchase) {
          await axios.put(`/api/purchases/${editingPurchase.id}`, payload);
        } else {
          await axios.post('/api/purchases', payload);
        }
      }

      setShowModal(false);
      setEditingPurchase(null);
      setFormData(emptyForm());
      clearAttachmentState();
      await fetchAll();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to save purchase');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (purchase) => {
    setEditingPurchase(purchase);
    clearAttachmentState();
    if (purchase.has_attachment || purchase.attachment_filename) {
      setScanInfo(`Saved bill: ${purchase.attachment_original_name || 'attachment'}`);
    }
    setFormData({
      supplier_id: purchase.supplier_id || '',
      purchase_date: (purchase.purchase_date || '').substring(0, 10),
      discount: purchase.discount || 0,
      notes: purchase.notes || '',
      payment_status: purchase.payment_status || 'pending',
      items: (purchase.items || []).length
        ? purchase.items.map((item) => ({
            mode: 'existing',
            product_id: item.product_id || '',
            description: item.description || '',
            quantity: item.quantity || 1,
            unit_cost: item.unit_cost || 0,
            discount: item.discount || 0,
            new_name: '',
            new_sku: '',
            new_price: 0,
            new_unit: 'pcs',
            uom_id: item.uom_id || '',
            sale_price: item.sale_price != null ? item.sale_price : ''
          }))
        : [emptyItem()]
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this purchase and reverse its stock?')) return;
    try {
      await axios.delete(`/api/purchases/${id}`);
      fetchAll();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to delete purchase');
    }
  };

  const closeAttachmentPreview = () => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = '';
    }
    setAttachmentPreview(null);
  };

  const viewAttachment = async (purchase) => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = '';
    }
    const name = purchase.attachment_original_name || 'Bill attachment';
    setAttachmentPreview({ loading: true, url: '', name, kind: 'image', error: '' });
    try {
      const res = await axios.get(`/api/purchases/${purchase.id}/attachment`, {
        responseType: 'blob'
      });
      const headerType = String(res.headers['content-type'] || '').split(';')[0].trim().toLowerCase();
      if (headerType === 'application/json') {
        const text = await res.data.text();
        let message = 'Failed to open attachment';
        try {
          message = JSON.parse(text).message || message;
        } catch (parseError) {
          // Keep the fallback message.
        }
        setAttachmentPreview({ loading: false, url: '', name, kind: 'image', error: message });
        return;
      }
      const type = headerType && headerType !== 'application/octet-stream'
        ? headerType
        : (purchase.attachment_mime || 'image/jpeg');
      const blob = res.data.type === type ? res.data : new Blob([res.data], { type });
      const url = URL.createObjectURL(blob);
      previewUrlRef.current = url;
      const kind = type.includes('pdf') ? 'pdf' : 'image';
      setAttachmentPreview({ loading: false, url, name, kind, error: '' });
    } catch (error) {
      const message = await attachmentErrorMessage(error);
      setAttachmentPreview({ loading: false, url: '', name, kind: 'image', error: message });
    }
  };

  const openModal = () => {
    setEditingPurchase(null);
    setFormData(emptyForm());
    clearAttachmentState();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingPurchase(null);
    clearAttachmentState();
  };

  if (loading) {
    return <div className="page-container"><div className="container">Loading...</div></div>;
  }

  return (
    <div className="page-container">
      <div className="container">
        <div className="page-header">
          <h1>Purchases</h1>
          <button onClick={openModal} className="btn btn-primary">
            New Purchase
          </button>
        </div>

        <div className="card">
          <table className="table">
            <thead>
              <tr>
                <th>Purchase #</th>
                <th>Supplier</th>
                <th>Date</th>
                <th>Items</th>
                <th>Total</th>
                <th>Payment</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {purchases.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center' }}>No purchases found</td>
                </tr>
              ) : (
                purchases.map((purchase) => (
                  <tr key={purchase.id}>
                    <td>{purchase.purchase_number}</td>
                    <td>{purchase.supplier_name || '-'}</td>
                    <td>{formatDate(purchase.purchase_date)}</td>
                    <td>{purchase.item_count || (purchase.items || []).length}</td>
                    <td>{formatCurrency(purchase.total_amount)}</td>
                    <td>
                      <span className={`status-badge status-${purchase.payment_status || 'pending'}`}>
                        {purchase.payment_status || 'pending'}
                      </span>
                    </td>
                    <td>
                      <KebabMenu
                        menuId={`purchase-${purchase.id}`}
                        openId={openActionMenu}
                        setOpenId={setOpenActionMenu}
                        items={[
                          (purchase.has_attachment || purchase.attachment_filename) && {
                            label: 'View',
                            icon: MENU_ICONS.view,
                            onClick: () => viewAttachment(purchase)
                          },
                          { label: 'Edit', icon: MENU_ICONS.edit, onClick: () => handleEdit(purchase) },
                          { label: 'Delete', icon: MENU_ICONS.trash, danger: true, onClick: () => handleDelete(purchase.id) }
                        ]}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {showModal && (
          <div className="modal">
            <div className="modal-content purchase-modal">
              <div className="modal-header">
                <h2>{editingPurchase ? 'Edit Purchase' : 'New Purchase'}</h2>
                <button className="close-btn" onClick={closeModal}>×</button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="purchase-scan-box">
                  <div className="form-group" style={{ marginBottom: 8 }}>
                    <label>Bill attachment (image / PDF)</label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={handleAttachmentChange}
                    />
                  </div>
                  <div className="purchase-scan-actions">
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={handleScanBill}
                      disabled={scanning || !attachmentFile}
                    >
                      {scanning ? 'Scanning…' : 'Scan bill & fill items'}
                    </button>
                    {editingPurchase && (editingPurchase.has_attachment || editingPurchase.attachment_filename) && (
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => viewAttachment(editingPurchase)}
                      >
                        View saved bill
                      </button>
                    )}
                  </div>
                  {scanInfo ? <p className="purchase-scan-info">{scanInfo}</p> : null}
                  <p className="purchase-hint" style={{ marginTop: 8 }}>
                    Scan reads the bill with Gemini. Existing products are matched by name/SKU; unmatched lines become new products. The file is saved with the purchase.
                  </p>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px' }}>
                  <div className="form-group">
                    <label>Supplier</label>
                    <div className="purchase-supplier-row">
                      <select
                        value={formData.supplier_id}
                        onChange={(e) => {
                          setFormData({ ...formData, supplier_id: e.target.value });
                          if (e.target.value) setScannedSupplierName('');
                        }}
                      >
                        <option value="">Select supplier</option>
                        {suppliers
                          .filter((s) => s.status === 'active' || s.id === formData.supplier_id)
                          .map((s) => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                          ))}
                      </select>
                      <button
                        type="button"
                        className="btn btn-success btn-sm"
                        onClick={handleAddSupplierManual}
                        disabled={addingSupplier}
                      >
                        {addingSupplier ? 'Adding…' : 'Add'}
                      </button>
                    </div>
                    {scannedSupplierName && !formData.supplier_id ? (
                      <div className="purchase-new-supplier">
                        <span>
                          New supplier on bill: <strong>{scannedSupplierName}</strong>
                        </span>
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          onClick={handleAddScannedSupplier}
                          disabled={addingSupplier}
                        >
                          {addingSupplier ? 'Adding…' : 'Add this supplier'}
                        </button>
                      </div>
                    ) : null}
                  </div>
                  <div className="form-group">
                    <label>Purchase Date *</label>
                    <DateInput
                      value={formData.purchase_date}
                      onChange={(e) => setFormData({ ...formData, purchase_date: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Payment status *</label>
                    <select
                      value={formData.payment_status}
                      onChange={(e) => setFormData({ ...formData, payment_status: e.target.value })}
                      required
                    >
                      <option value="pending">Pending</option>
                      <option value="billed">Billed</option>
                      <option value="paid">Paid</option>
                    </select>
                  </div>
                </div>

                <div style={{ marginTop: '16px', marginBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <strong>Items</strong>
                  <button type="button" onClick={addItem} className="btn btn-success btn-sm">
                    Add Item
                  </button>
                </div>

                {formData.items.map((item, index) => (
                  <div key={index} className="purchase-item-row">
                    <div className="purchase-item-mode">
                      <label>
                        <input
                          type="radio"
                          name={`mode-${index}`}
                          checked={item.mode === 'existing'}
                          onChange={() => updateItem(index, 'mode', 'existing')}
                        />
                        Existing product
                      </label>
                      <label>
                        <input
                          type="radio"
                          name={`mode-${index}`}
                          checked={item.mode === 'new'}
                          onChange={() => updateItem(index, 'mode', 'new')}
                        />
                        New product
                      </label>
                    </div>

                    {item.mode === 'existing' ? (
                      <>
                      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '10px' }}>
                        <div className="form-group">
                          <label>Product *</label>
                          <select
                            value={item.product_id}
                            onChange={(e) => updateItem(index, 'product_id', e.target.value)}
                            required={item.mode === 'existing'}
                          >
                            <option value="">Select product</option>
                            {products.map((p) => (
                              <option key={p.id} value={p.id}>
                                {p.name} (stock: {p.stock_quantity || 0} {p.unit || ''})
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="form-group">
                          <label>Sale price</label>
                          <input
                            type="number"
                            min="0"
                            step="0.001"
                            value={item.sale_price}
                            onChange={(e) => updateItem(index, 'sale_price', e.target.value)}
                            title="Price for this new stock. Pieces already in stock keep their old price."
                          />
                        </div>
                        <div className="form-group">
                          <label>UOM</label>
                          <select
                            value={item.uom_id || ''}
                            onChange={(e) => updateItem(index, 'uom_id', e.target.value)}
                            disabled={!item.product_id}
                          >
                            <option value="">Default</option>
                            {(products.find((p) => p.id === item.product_id)?.uoms || []).map((u) => (
                              <option key={u.id} value={u.id}>
                                {u.code}{Number(u.is_base) ? ' (base)' : ` ×${u.conversion_to_base}`}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      {(() => {
                        const product = products.find((p) => p.id === item.product_id);
                        const hint = product ? stockPriceHint(product, item.sale_price, item.uom_id) : '';
                        return hint ? (
                          <div style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 600, color: '#9a3412' }}>{hint}</div>
                        ) : null;
                      })()}
                      </>
                    ) : (
                      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '10px' }}>
                        <div className="form-group">
                          <label>Product name *</label>
                          <input
                            type="text"
                            value={item.new_name}
                            onChange={(e) => updateItem(index, 'new_name', e.target.value)}
                            required={item.mode === 'new'}
                          />
                        </div>
                        <div className="form-group">
                          <label>SKU</label>
                          <input
                            type="text"
                            value={item.new_sku}
                            onChange={(e) => updateItem(index, 'new_sku', e.target.value)}
                          />
                        </div>
                        <div className="form-group">
                          <label>Sale price</label>
                          <input
                            type="number"
                            min="0"
                            step="0.001"
                            value={item.new_price}
                            onChange={(e) => updateItem(index, 'new_price', e.target.value)}
                          />
                        </div>
                        <div className="form-group">
                          <label>Unit</label>
                          <input
                            type="text"
                            value={item.new_unit}
                            onChange={(e) => updateItem(index, 'new_unit', e.target.value)}
                          />
                        </div>
                      </div>
                    )}

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr auto', gap: '10px', alignItems: 'end' }}>
                      <div className="form-group">
                        <label>Qty *</label>
                        <input
                          type="number"
                          min="1"
                          step="1"
                          value={item.quantity}
                          onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Unit cost *</label>
                        <input
                          type="number"
                          min="0"
                          step="0.001"
                          value={item.unit_cost}
                          onChange={(e) => updateItem(index, 'unit_cost', e.target.value)}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Discount</label>
                        <input
                          type="number"
                          min="0"
                          step="0.001"
                          value={item.discount}
                          onChange={(e) => updateItem(index, 'discount', e.target.value)}
                        />
                      </div>
                      <div className="form-group">
                        <label>Line total</label>
                        <div className="purchase-line-total">{formatCurrency(lineTotal(item))}</div>
                      </div>
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => removeItem(index)}
                        disabled={formData.items.length === 1}
                        style={{ marginBottom: '16px' }}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginTop: '10px' }}>
                  <div className="form-group">
                    <label>Order discount</label>
                    <input
                      type="number"
                      min="0"
                      step="0.001"
                      value={formData.discount}
                      onChange={(e) => setFormData({ ...formData, discount: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Notes</label>
                    <input
                      type="text"
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    />
                  </div>
                </div>

                <div className="purchase-totals">
                  <div><span>Subtotal</span><strong>{formatCurrency(totals.subtotal)}</strong></div>
                  <div><span>Total</span><strong>{formatCurrency(totals.total)}</strong></div>
                </div>

                <p className="purchase-hint">
                  Saving will add quantity to product stock. New products are created automatically with stock from this purchase.
                </p>

                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '16px' }}>
                  <button type="button" onClick={closeModal} className="btn btn-secondary">
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={saving || scanning}>
                    {saving ? 'Saving...' : (editingPurchase ? 'Update Purchase' : 'Create Purchase')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {attachmentPreview && (
          <div className="modal purchase-attachment-backdrop" onClick={closeAttachmentPreview}>
            <div
              className="modal-content purchase-attachment-modal"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="modal-header">
                <h2>{attachmentPreview.name}</h2>
                <button type="button" className="close-btn" onClick={closeAttachmentPreview}>×</button>
              </div>
              {attachmentPreview.loading ? (
                <p className="purchase-hint">Loading attachment…</p>
              ) : attachmentPreview.error ? (
                <p className="purchase-attachment-error">{attachmentPreview.error}</p>
              ) : attachmentPreview.kind === 'pdf' ? (
                <iframe
                  className="purchase-attachment-frame"
                  src={attachmentPreview.url}
                  title={attachmentPreview.name}
                />
              ) : (
                <img
                  className="purchase-attachment-image"
                  src={attachmentPreview.url}
                  alt={attachmentPreview.name}
                  onError={() => setAttachmentPreview((current) => (
                    current ? { ...current, error: 'Could not display this attachment' } : current
                  ))}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Purchases;
